import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { AppShell } from './components/AppShell';
import { LoginPage } from './components/LoginPage';
import { ChatPage } from './pages/ChatPage';
import { NewPage } from './pages/NewPage';

// Low-frequency pages: lazy-import so they don't weigh the initial
// bundle. React.lazy needs a default export, so adapter each one.
const SettingsPageLazy = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const ProjectsPageLazy = lazy(() =>
  import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const ProjectPageLazy = lazy(() =>
  import('./pages/ProjectPage').then((m) => ({ default: m.ProjectPage })),
);
const FilesPageLazy = lazy(() =>
  import('./pages/FilesPage').then((m) => ({ default: m.FilesPage })),
);
const SearchPageLazy = lazy(() =>
  import('./pages/SearchPage').then((m) => ({ default: m.SearchPage })),
);
const AdminPageLazy = lazy(() =>
  import('./pages/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const MacScreenPageLazy = lazy(() =>
  import('./pages/MacScreenPage').then((m) => ({ default: m.MacScreenPage })),
);
const McpPageLazy = lazy(() => import('./pages/McpPage').then((m) => ({ default: m.McpPage })));

function LazyBoundary({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/new' });
  },
});

const newRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/new',
  component: NewPage,
});

export const chatRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/chat/$sessionId',
  component: ChatPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: () => (
    <LazyBoundary>
      <SettingsPageLazy />
    </LazyBoundary>
  ),
});

const filesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/files',
  component: () => (
    <LazyBoundary>
      <FilesPageLazy />
    </LazyBoundary>
  ),
});

const projectsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects',
  component: () => (
    <LazyBoundary>
      <ProjectsPageLazy />
    </LazyBoundary>
  ),
});

export const projectRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/projects/$projectId',
  component: () => (
    <LazyBoundary>
      <ProjectPageLazy />
    </LazyBoundary>
  ),
});

const searchRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/search',
  component: () => (
    <LazyBoundary>
      <SearchPageLazy />
    </LazyBoundary>
  ),
});

const mcpRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mcp',
  component: () => (
    <LazyBoundary>
      <McpPageLazy />
    </LazyBoundary>
  ),
});

// «Экран Мака» (admin, needs MAC_SCREEN_URL); the page checks access itself.
const macScreenRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/mac',
  component: () => (
    <LazyBoundary>
      <MacScreenPageLazy />
    </LazyBoundary>
  ),
});

// Admin-only. The page component itself bounces non-admins (it reads
// the auth context, which the router can't); this route just lazy-loads
// the panel.
const adminRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/admin',
  component: () => (
    <LazyBoundary>
      <AdminPageLazy />
    </LazyBoundary>
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  appLayoutRoute.addChildren([
    indexRoute,
    newRoute,
    chatRoute,
    filesRoute,
    projectsRoute,
    projectRoute,
    searchRoute,
    mcpRoute,
    macScreenRoute,
    adminRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
